import React, { useEffect, useState, useRef } from 'react'
import { Link, useLocation } from 'react-router-dom'
import DockNav from './DockNav'
import RiveButton from './RiveButton'

export default function Header() {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false)
  const [expandedMobileItem, setExpandedMobileItem] = useState(null)
  const location = useLocation()
  const logoRef = useRef(null)
  const navRefs = useRef([])
  const mobileMenuRef = useRef(null)

  // Close mobile menu on route change
  useEffect(() => {
    closeMobileMenu()
  }, [location.pathname])

  // Magnet effect implementation from old project
  useEffect(() => {
    const navTitles = [logoRef.current, ...navRefs.current].filter(Boolean)

    navTitles.forEach(function (navTitle) {
      if (!navTitle) return

      const originalTransform = window.getComputedStyle(navTitle).transform

      const handleMouseMove = (e) => {
        const rect = navTitle.getBoundingClientRect()
        const x = e.clientX - rect.left
        const y = e.clientY - rect.top
        const titleWidth = navTitle.clientWidth
        const titleHeight = navTitle.clientHeight
        const moveX = (x / titleWidth - 0.5) * 40 // Adjust the multiplier as needed
        const moveY = (y / titleHeight - 0.5) * 40 // Adjust the multiplier as needed
        navTitle.style.transform = `translate(${moveX}px, ${moveY}px)`
      }

      const handleMouseLeave = () => {
        navTitle.style.transform = originalTransform
      }

      navTitle.addEventListener('mousemove', handleMouseMove)
      navTitle.addEventListener('mouseleave', handleMouseLeave)

      return () => {
        navTitle.removeEventListener('mousemove', handleMouseMove)
        navTitle.removeEventListener('mouseleave', handleMouseLeave)
      }
    })
  }, [])

  const navItems = [
    { href: '/about', label: 'About' },
    {
      href: '/projects',
      label: 'Projects',
      hasDropdown: true,
      dropdownItems: [
        {
          icon: '⌘',
          title: 'Website Design & Development',
          description: 'Stunning, high-performance websites designed to captivate and convert.',
        },
        {
          icon: '○',
          title: 'Logo Design & Branding',
          description: 'Strategic, eye-catching logos to define your brand.',
        },
        {
          icon: '◇',
          title: 'Graphic Design',
          description: 'Establishing confidence and industry leadership through brand design.',
        },
        {
          icon: '△',
          title: 'Video',
          description:
            'Captivating brand stories, testimonials and promo videos that champion your brand.',
        },
        {
          icon: '□',
          title: 'Photography',
          description:
            'Every angle, every story, we capture compelling narratives through imagery.',
        },
      ],
    },
  ]

  const handleConnectClick = () => {
    console.log('Connect button clicked!')
    try {
      // Navigate to the connect page
      window.location.href = '/connect'
      closeMobileMenu()
    } catch (error) {
      console.error('Error navigating to connect page:', error)
    }
  }

  const closeMobileMenu = () => {
    setIsMobileMenuOpen(false)
    setExpandedMobileItem(null)
  }

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen)
    setExpandedMobileItem(null)
  }

  return (
    <header className="fixed top-0 left-0 right-0 z-50 bg-transparent">
      
      <div className="container mx-auto py-4 md:py-4 px-4 md:px-0 relative">
        <div className="flex items-center justify-between">
          {/* Left: HS Logo */}
          <Link
            to="/"
            ref={logoRef}
            id="creditTop"
            className="hs-logo cursor-pointer"
          >
            HS
          </Link>

          {/* Center: Dock Navigation - Perfectly centered - Hidden on mobile */}
          <div className="absolute left-1/2 transform -translate-x-1/2 hidden md:block">
            <DockNav />
          </div>

          {/* Right: Rive Connect Button - Hidden on mobile */}
          <div className="hidden md:block">
            <RiveButton
              src="/animations/cat_button.riv"
              onClick={handleConnectClick}
              width={180}
              height={90}
              className="nav-links-title connect-button -mt-1"
            />
          </div>

          {/* Mobile Search and Hamburger Menu */}
          <div className="lg:hidden flex items-center space-x-2">
            {/* Mobile Search Bar */}
            <div className="relative">
              <input
                type="text"
                placeholder="Search..."
                className="w-36 md:w-32 px-3 py-2 md:px-3 md:py-1.5 bg-black text-white placeholder-gray-400 rounded-2xl focus:outline-none transition-all duration-200 text-base md:text-sm"
                style={{ border: '0.3px solid #666666' }}
                onMouseEnter={(e) =>
                  (e.target.style.border = '0.3px solid #E06100')
                }
                onMouseLeave={(e) =>
                  (e.target.style.border = '0.3px solid #666666')
                }
                onClick={() => {
                  // Open search modal
                  const event = new CustomEvent('openHeaderSearch')
                  document.dispatchEvent(event)
                }}
                readOnly
              />
            </div>

            {/* Mobile Hamburger Menu Button */}
            <button
              onClick={toggleMobileMenu}
              className="text-white p-3 md:p-2 focus:outline-none transition-all duration-200"
              aria-label={isMobileMenuOpen ? 'Close mobile menu' : 'Open mobile menu'}
              aria-expanded={isMobileMenuOpen}
            >
              <div className="w-7 h-7 md:w-6 md:h-6 flex items-center justify-center">
                <svg
                  className={`w-6 h-6 md:w-5 md:h-5 transition-all duration-300 ease-in-out ${
                    isMobileMenuOpen ? 'rotate-45' : 'rotate-0'
                  }`}
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 4v16m8-8H4"
                  />
                </svg>
              </div>
            </button>
          </div>
        </div>

        {/* Mobile Menu Backdrop */}
        {isMobileMenuOpen && (
          <div
            className="lg:hidden fixed top-[80px] left-0 right-0 bottom-0 bg-black z-[9998] pointer-events-none"
            style={{
              backgroundColor: '#000000 !important',
              background: '#000000 !important',
              opacity: '1 !important',
            }}
          />
        )}

        {/* Mobile Menu Dropdown */}
        {isMobileMenuOpen && (
          <div
            ref={mobileMenuRef}
            className="lg:hidden fixed top-[80px] left-0 right-0 bottom-0 bg-black z-[9999] flex flex-col mobile-menu-overlay"
            style={{
              backgroundColor: '#000000 !important',
              background: '#000000 !important',
              opacity: '1 !important',
              zIndex: 9999,
              height: 'calc(100vh - 80px)',
              minHeight: 'calc(100vh - 80px)',
            }}
          >
            {/* Main Navigation and Connect Button */}
            <div className="flex-1 flex flex-col justify-start px-6 pt-16">
              <nav className="space-y-8">
                {navItems.map((item) => (
                  <div key={item.href}>
                    <Link
                      to={item.href}
                      className="block text-white text-2xl font-medium hover:text-[#E06100] transition-colors duration-200 font-syne text-left cursor-pointer"
                      onClick={closeMobileMenu}
                    >
                      {item.label}
                    </Link>
                  </div>
                ))}
              </nav>

              {/* Connect Button */}
              <div className="mt-8 relative z-[10000] text-center">
                <div
                  className={`w-full transition-all duration-200 ${
                    isMobileMenuOpen ? 'scale-105' : 'scale-100'
                  }`}
                  style={{
                    transitionDelay: isMobileMenuOpen ? '0.2s' : '0s',
                  }}
                >
                  <button
                    onClick={handleConnectClick}
                    className="w-full bg-[#E06100] text-white py-4 px-6 rounded-lg font-medium font-syne hover:bg-[#E06100]/90 transition-all duration-200 transform hover:scale-105 active:scale-95"
                  >
                    Let's Connect
                  </button>
                </div>
              </div>

              {/* Socials Section */}
              <div className="mt-12 border-t border-gray-800 pt-8">
                <div className="text-gray-400 text-lg font-syne mb-6 text-center">Socials</div>
                <div className="flex flex-col space-y-4">
                  <a
                    href="https://www.instagram.com/hemanshakthi"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center justify-between text-white font-syne hover:text-[#E06100] active:text-[#E06100] transition-colors duration-200"
                    onClick={closeMobileMenu}
                  >
                    <span>instagram</span>
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"
                      />
                    </svg>
                  </a>
                  <a
                    href="https://www.linkedin.com/in/heman-shakthi/"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center justify-between text-white font-syne hover:text-[#E06100] active:text-[#E06100] transition-colors duration-200"
                    onClick={closeMobileMenu}
                  >
                    <span>linkedin</span>
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"
                      />
                    </svg>
                  </a>
                  <a
                    href="mailto:shakthiheman2@gmail.com"
                    className="flex items-center justify-between text-white font-syne hover:text-[#E06100] active:text-[#E06100] transition-colors duration-200"
                    onClick={closeMobileMenu}
                  >
                    <span>email</span>
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"
                      />
                    </svg>
                  </a>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </header>
  )
}